import axios from 'axios';
import { validateResponse } from './validateResponse';
import { getApiErrorMessage } from './errorUtils';
import {
  RecurringDefListResponseSchema,
  TaskDetailResponseSchema,
  TaskHistoryResponseSchema,
  TaskListResponseSchema,
  TaskAssigneesResponseSchema,
  TaskTagsResponseSchema,
  type TaskDetailDto,
  type TaskListRowDto,
} from '../schemas/tasks.schemas';
import type {
  RecurringTaskDef,
  TaskAssignee,
  TaskLifecycleAction,
  TaskTag,
} from '@/shared/types/taskQueue.types';
import type { TaskStatus } from '@/shared/types/taskQueue.types';

const BASE = '/api/tasks';

export interface ListTasksParams {
  status?: TaskStatus | TaskStatus[];
  tags?: string | string[];
  assignee?: 'mine' | 'unassigned';
  source?: 'one_time' | 'recurring';
  createdByMe?: boolean;
  q?: string;
}

export async function listTasks(params: ListTasksParams = {}): Promise<TaskListRowDto[]> {
  const response = await axios.get(BASE, { params });
  const parsed = validateResponse(TaskListResponseSchema, response.data, 'GET /api/tasks');
  return parsed.tasks;
}

export async function getTask(id: string): Promise<TaskDetailDto> {
  const response = await axios.get(`${BASE}/${id}`);
  const parsed = validateResponse(TaskDetailResponseSchema, response.data, 'GET /api/tasks/:id');
  return parsed.task;
}

export async function createTask(body: {
  title: string;
  description?: string | null;
  spec?: string | null;
  tagIds?: string[];
  priority?: number;
  slackPermalink?: string | null;
}): Promise<TaskDetailDto> {
  const response = await axios.post(BASE, body);
  const parsed = validateResponse(TaskDetailResponseSchema, response.data, 'POST /api/tasks');
  return parsed.task;
}

export async function updateTask(
  id: string,
  body: {
    title?: string;
    description?: string | null;
    spec?: string | null;
    tagIds?: string[];
    priority?: number;
    slackPermalink?: string | null;
  },
): Promise<TaskDetailDto> {
  const response = await axios.patch(`${BASE}/${id}`, body);
  const parsed = validateResponse(TaskDetailResponseSchema, response.data, 'PATCH /api/tasks/:id');
  return parsed.task;
}

export async function taskLifecycle(
  id: string,
  action: TaskLifecycleAction,
  body?: { assigneeId?: string },
): Promise<TaskDetailDto> {
  const response = await axios.post(`${BASE}/${id}/${action}`, body ?? {});
  const parsed = validateResponse(TaskDetailResponseSchema, response.data, `POST /api/tasks/:id/${action}`);
  return parsed.task;
}

export async function approveTasksBulk(taskIds: string[]): Promise<number> {
  const response = await axios.post(`${BASE}/approve-bulk`, { taskIds });
  return Number(response.data.approved) || 0;
}

export async function archiveTasksBulk(taskIds: string[]): Promise<number> {
  const response = await axios.post(`${BASE}/archive-bulk`, { taskIds });
  return Number(response.data.archived) || 0;
}

export async function addTaskComment(taskId: string, body: string): Promise<void> {
  await axios.post(`${BASE}/${taskId}/comments`, { body });
}

export async function getTaskHistory(taskId: string) {
  const response = await axios.get(`${BASE}/${taskId}/history`);
  return validateResponse(TaskHistoryResponseSchema, response.data, 'GET /api/tasks/:id/history').history;
}

export async function listTaskTags(): Promise<TaskTag[]> {
  const response = await axios.get(`${BASE}/tags`);
  return validateResponse(TaskTagsResponseSchema, response.data, 'GET /api/tasks/tags').tags;
}

export async function listTaskAssignees(): Promise<TaskAssignee[]> {
  const response = await axios.get(`${BASE}/assignees`);
  return validateResponse(TaskAssigneesResponseSchema, response.data, 'GET /api/tasks/assignees')
    .assignees;
}

export async function createTaskTag(name: string): Promise<TaskTag> {
  const response = await axios.post(`${BASE}/tags`, { name });
  return response.data.tag as TaskTag;
}

export async function updateTaskTag(id: string, patch: { name?: string; retired?: boolean }): Promise<TaskTag> {
  const response = await axios.patch(`${BASE}/tags/${id}`, patch);
  return response.data.tag as TaskTag;
}

export async function listRecurringDefs(): Promise<RecurringTaskDef[]> {
  const response = await axios.get(`${BASE}/recurring-defs`);
  const parsed = validateResponse(
    RecurringDefListResponseSchema,
    response.data,
    'GET /api/tasks/recurring-defs',
  );
  return parsed.defs as RecurringTaskDef[];
}

export async function createRecurringDef(body: {
  title: string;
  description?: string | null;
  spec?: string | null;
  cadence: 'daily' | 'weekly' | 'monthly';
  tagIds?: string[];
  priority?: number;
}): Promise<RecurringTaskDef> {
  const response = await axios.post(`${BASE}/recurring-defs`, body);
  return response.data.def as RecurringTaskDef;
}

export async function updateRecurringDef(
  id: string,
  body: Record<string, unknown>,
): Promise<RecurringTaskDef> {
  const response = await axios.patch(`${BASE}/recurring-defs/${id}`, body);
  return response.data.def as RecurringTaskDef;
}

export async function recurringDefAction(
  id: string,
  action: 'approve' | 'pause' | 'resume' | 'archive',
): Promise<RecurringTaskDef | void> {
  const response = await axios.post(`${BASE}/recurring-defs/${id}/${action}`);
  if (action === 'archive') return;
  return response.data.def as RecurringTaskDef;
}

export { getApiErrorMessage };
